import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { galleryImages } from "@/lib/data/gallery";

// Get base path from Vite config
const basePath = import.meta.env.BASE_URL;

export default function Home() {
  const featuredImages = galleryImages.slice(0, 6);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${basePath}images/hero/hero-main.jpg')`,
        }}
      >
        <div className="text-center text-white px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-medium mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Alex Chen Photography
          </h1>
          <p className="text-xl sm:text-2xl mb-8 font-light">
            Capturing moments, creating memories
          </p>
          <p className="text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
            Professional photographer specializing in portraits, landscapes and
            editorial work. Based in Los Angeles, available worldwide for
            commissioned projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center bg-white text-gray-900 px-8 py-3 rounded-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              View Gallery
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-sm font-medium hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-inter"
            >
              Featured Work
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              A curated selection of recent projects showcasing diverse
              photographic styles and subjects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredImages.map((image) => (
              <div key={image.id} className="group cursor-pointer">
                <div className="relative overflow-hidden bg-gray-100 aspect-[4/3] mb-4">
                  <img
                    src={image.image}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 font-inter leading-tight">
                    {image.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-tight">
                    {image.category} | {image.year}
                  </p>
                  <p className="text-sm text-gray-700 leading-tight mt-2">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/gallery"
              className="inline-flex items-center text-gray-900 font-medium hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Work
              <div className="w-4 h-4 ml-2 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 font-inter"
              >
                About Alex Chen
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                With over a decade of experience in professional photography, I
                specialize in capturing authentic moments and creating
                compelling visual narratives. My work spans from intimate
                portraits to grand landscapes, always seeking to reveal the
                extraordinary in the ordinary.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Based in Los Angeles but available worldwide, I bring a unique
                perspective shaped by diverse cultural experiences and a passion
                for storytelling through imagery.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-gray-900 font-medium hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Learn More
                <div className="w-4 h-4 ml-2 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="relative">
              <img
                src={`${basePath}images/about/profile-home.jpg`}
                alt="Alex Chen, Professional Photographer"
                className="w-full h-auto object-cover object-top rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 font-inter"
          >
            Ready to Create Something Beautiful?
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Whether you need portraits, event coverage, or editorial work, let's
            discuss how we can bring your vision to life through compelling
            photography.
          </p>
          <Link
            to="/contact"
            className="bg-gray-900 text-white px-8 py-3 rounded-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Start a Project
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
